import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import "@/styles/admin/PlanSettings.css";

import {
  getPlanFetureSettings,
  addPlanFeatures,
} from "../../actions/subscriptionPlanAction";
import { showErrorMessage, showSuccessMessage } from "../../utils/common";
import SubHeader from "../../components/SubHeader";

const styleError = { color: "red", display: "block"};

// Error key helper: "existing-0-feature", "new-2-Gold"
const errKey = (type, index, field) => `${type}-${index}-${field}`;

function PlanSettings() {
  const dispatch = useDispatch();

  const [plans, setPlans] = useState([]);
  const [planData, setPlanData] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const emptyValues = (planList) =>
    planList.reduce((acc, p) => ({ ...acc, [p.name]: "" }), {});

  /* ---------------- FETCH ---------------- */
  const fetchPlanFetureSettings = async () => {
    try {
      setLoading(true);

      const response = await dispatch(getPlanFetureSettings());
      const allPlans = response?.data || [];

      const planList = [];
      const featureMap = {};

      allPlans.forEach((item) => {
        let planName;
        let features;
        let planId;

        if (Array.isArray(item?.features) && item?.plan_name) {
          planName = item.plan_name;
          features = item.features;
          planId = item.plan_id ?? item.id;
        } else {
          const [name, list] = Object.entries(item || {})[0] || [];
          planName = name;
          features = Array.isArray(list) ? list : [];
          planId = item.plan_id ?? item.id;
        }

        if (!planName) return;
        if (planId === undefined) planId = features[0]?.plan_id;

        planList.push({ name: planName, id: planId });

        features.forEach((f) => {
          if (!f?.key_name) return;
          if (!featureMap[f.key_name]) featureMap[f.key_name] = {};
          featureMap[f.key_name][planName] = f.key_value ?? "";
        });
      });

      const rows = Object.keys(featureMap).map((feature) => ({
        feature,
        values: planList.reduce(
          (acc, p) => ({ ...acc, [p.name]: featureMap[feature][p.name] ?? "" }),
          {}
        ),
      }));

      setPlans(planList);
      setPlanData(rows);
      setErrors({});
    } catch (error) {
      console.error("GET Plan Feature Settings Error:", error);
      // showErrorMessage("Failed to load plan settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanFetureSettings();
  }, []);

  /* ---------------- VALIDATION ---------------- */
  const clearError = (key) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  // Returns an errors object. Empty object = valid.
  const validateAll = () => {
    const newErrors = {};

    const checkRows = (rows, type) => {
      rows.forEach((row, index) => {
        // Feature name
        if (!row.feature?.trim()) {
          newErrors[errKey(type, index, "feature")] = "The field is required";
        }

        // Each plan value
        plans.forEach((plan) => {
          const val = row.values?.[plan.name];
          if (val === undefined || val === null || !String(val).trim()) {
            newErrors[errKey(type, index, plan.name)] = `The field is required`;
          }
        });
      });
    };

    checkRows(planData, "existing");
    checkRows(newRows, "new");

    // Duplicate feature names (case-insensitive) across existing + new rows
    const seen = {};
    const allRows = [
      ...planData.map((r, i) => ({ r, type: "existing", i })),
      ...newRows.map((r, i) => ({ r, type: "new", i })),
    ];

    allRows.forEach(({ r, type, i }) => {
      const name = r.feature?.trim().toLowerCase();
      if (!name) return;

      if (seen[name]) {
        // mark both the first occurrence and this one
        const first = seen[name];
        const firstKey = errKey(first.type, first.i, "feature");
        if (!newErrors[firstKey]) newErrors[firstKey] = "Duplicate feature name";
        const key = errKey(type, i, "feature");
        if (!newErrors[key]) newErrors[key] = "Duplicate feature name";
      } else {
        seen[name] = { type, i };
      }
    });

    return newErrors;
  };

  /* ---------------- HANDLERS ---------------- */
  const handleChange = (index, field, value) => {
    clearError(errKey("existing", index, field));
    setPlanData((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        if (field === "feature") return { ...row, feature: value };
        return { ...row, values: { ...row.values, [field]: value } };
      })
    );
  };

  const addPlan = () => {
    setNewRows((prev) => [...prev, { feature: "", values: emptyValues(plans) }]);
  };

  const handleNewRowChange = (index, field, value) => {
    clearError(errKey("new", index, field));
    setNewRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        if (field === "feature") return { ...row, feature: value };
        return { ...row, values: { ...row.values, [field]: value } };
      })
    );
  };

  // Deleting shifts indexes, so drop errors for that row type and let
  // the next Save re-validate.
  const clearErrorsFor = (type) =>
    setErrors((prev) =>
      Object.keys(prev).reduce((acc, k) => {
        if (!k.startsWith(`${type}-`)) acc[k] = prev[k];
        return acc;
      }, {})
    );

  const deleteRow = (index) => {
    clearErrorsFor("existing");
    setPlanData((prev) => prev.filter((_, i) => i !== index));
  };

  const deleteNewRow = (index) => {
    clearErrorsFor("new");
    setNewRows((prev) => prev.filter((_, i) => i !== index));
  };

  const createPostRows = () => {
    const allRows = [...planData, ...newRows];
    const postRows = [];

    allRows.forEach((row) => {
      if (!row.feature?.trim()) return;
      plans.forEach((plan) => {
        postRows.push({
          plan_id: plan.id,
          key_name: row.feature.trim(),
          key_value: String(row.values?.[plan.name] ?? "").trim(),
        });
      });
    });

    return postRows;
  };

  const handleSave = async () => {
    try {
      if (!plans.length) {
        showErrorMessage("No plans found");
        return;
      }

      const missingId = plans.find((p) => p.id === undefined || p.id === null);
      if (missingId) {
        showErrorMessage(
          `plan_id not found for "${missingId.name}". API must return plan_id.`
        );
        return;
      }

      if (!planData.length && !newRows.length) {
        showErrorMessage("Please add at least one feature");
        return;
      }

      const validationErrors = validateAll();
      setErrors(validationErrors);

      if (Object.keys(validationErrors).length) {
        // showErrorMessage("Please fix the highlighted fields");
        return;
      }

      const postRows = createPostRows();
      if (!postRows.length) {
        showErrorMessage("Please fill at least one feature");
        return;
      }

      setSaving(true);
      await dispatch(addPlanFeatures(postRows));
      await fetchPlanFetureSettings();

      setNewRows([]);
      setErrors({});
      showSuccessMessage("Changes saved successfully");
    } catch (error) {
      console.error("Save Error:", error);
      showErrorMessage("Something went wrong while saving");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- RENDER HELPERS ---------------- */
  const inputClass = (key) => (errors[key] ? "input-error" : "");
  const inputStyle = (key) => (errors[key] ? { borderColor: "red" } : undefined);

  const renderError = (key) =>
    errors[key] ? (
      <span className="company-required" style={styleError}>
        {errors[key]}
      </span>
    ) : null;

  const renderRow = (row, index, type) => {
    const isNew = type === "new";
    const onChange = isNew ? handleNewRowChange : handleChange;
    const onDelete = isNew ? deleteNewRow : deleteRow;
    const featureKey = errKey(type, index, "feature");

    return (
      <tr key={`${type}-${index}`}>
        <td>
          <input
            type="text"
            placeholder={isNew ? "Feature" : undefined}
            className={inputClass(featureKey)}
            style={inputStyle(featureKey)}
            value={row.feature || ""}
            onChange={(e) => onChange(index, "feature", e.target.value)}
          />
          {renderError(featureKey)}
        </td>

        {plans.map((plan) => {
          const valueKey = errKey(type, index, plan.name);
          return (
            <td key={plan.name}>
              <input
                type="text"
                placeholder={isNew ? plan.name : undefined}
                className={inputClass(valueKey)}
                style={inputStyle(valueKey)}
                value={row.values?.[plan.name] || ""}
                onChange={(e) => onChange(index, plan.name, e.target.value)}
              />
              {renderError(valueKey)}
            </td>
          );
        })}

        <td>
          <button
            type="button"
            className="subscription-plan-delete-btn"
            onClick={() => onDelete(index)}
          >
            <span className="tooltip1 tooltip-question fa-solid fa-trash-can icon-trash border-0">
              <span className="tooltiptext1 data-table-tooltiop">Delete</span>
            </span>
          </button>
        </td>
      </tr>
    );
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div className="subscription-plan-settings">
      <SubHeader
        layer1="Subscription Management"
        layer2="Plan Settings"
        layer3="List"
        pageName="Plan Settings"
        subTitle=""
        clickable={{ layer1: "", layer2: "", layer3: "" }}
      />

      <div className="subscription-plan-card">
        <div className="subscription-plan-header">
          <h2></h2>
          <button
            type="button"
            className="subscription-plan-add-btn"
            onClick={addPlan}
            disabled={!plans.length}
          >
            + Add Plans
          </button>
        </div>

        <div className="subscription-plan-table-wrapper">
          {loading ? (
            <p className="table-loading">Loading...</p>
          ) : (
            <table className="subscription-plan-table">
              <thead>
                <tr>
                  <th>Features</th>
                  {plans.map((plan) => (
                    <th key={plan.name}>{plan.name}</th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {planData.map((row, index) => renderRow(row, index, "existing"))}
                {newRows.map((row, index) => renderRow(row, index, "new"))}

                {!planData.length && !newRows.length && (
                  <tr>
                    <td colSpan={plans.length + 2}>
                      No features yet. Click "+ Add Plans" to add one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="subscription-plan-save-wrapper">
        <button
          type="button"
          className="subscription-plan-save-btn"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

export default PlanSettings;